import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  PanelRightOpen, 
  PanelRightClose, 
  User, 
  Phone, 
  Mail, 
  MessageSquare, 
  Clock, 
  ChevronRight,
  Activity,
  Users,
  Building2,
  RefreshCw
} from 'lucide-react';
import { formatPhone } from '../utils/formatPhone';
import EmailTrackingBadges from './EmailTrackingBadges';
import { fmtEST } from '../utils/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function RecentActivityPanel() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'activity'
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Load recently viewed leads from localStorage (most recent first)
      const storedLeads = JSON.parse(localStorage.getItem('recentLeads') || '[]');
      setRecentLeads(storedLeads.slice(0, 15));

      // Fetch recent communications
      const commsRes = await axios.get(`${API}/communications/recent?limit=15`);
      setRecentActivity(commsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch recent data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4 text-green-600" />;
      case 'email': return <Mail className="h-4 w-4 text-blue-600" />;
      case 'sms': return <MessageSquare className="h-4 w-4 text-purple-600" />;
      default: return <Activity className="h-4 w-4 text-slate-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call': return 'bg-green-100 border-green-200';
      case 'email': return 'bg-blue-100 border-blue-200';
      case 'sms': return 'bg-purple-100 border-purple-200';
      default: return 'bg-slate-100 border-slate-200';
    }
  };

  return (
    <>
      {/* Toggle Button - Fixed on right side */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-l-lg shadow-lg transition-all ${isOpen ? 'right-80' : 'right-0'}`}
        title={isOpen ? 'Close panel' : 'Recent Activity'}
        data-testid="recent-panel-toggle"
      >
        {isOpen ? <PanelRightClose className="h-5 w-5" /> : <PanelRightOpen className="h-5 w-5" />}
      </button>

      {/* Slide-out Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl border-l border-slate-200 z-30 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
        data-testid="recent-panel"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-600" />
              Quick Access
            </h2>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={fetchData}
              disabled={loading}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex mt-3 bg-slate-200 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('leads')}
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'leads' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="tab-recent-leads"
            >
              <Users className="h-4 w-4 inline mr-1" />
              Leads
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'activity' 
                  ? 'bg-white text-indigo-600 shadow-sm' 
                  : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="tab-recent-activity"
            >
              <Clock className="h-4 w-4 inline mr-1" />
              Activity
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-140px)]">
          {activeTab === 'leads' ? (
            <div className="p-2">
              {recentLeads.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">No recent leads</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentLeads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => {
                        navigate(`/admin/leads/${lead.id}`);
                        setIsOpen(false);
                      }}
                      className="w-full text-left p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors group"
                      data-testid={`recent-lead-${lead.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate group-hover:text-indigo-600">
                            {lead.first_name} {lead.last_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            {lead.company_name && (
                              <span className="flex items-center gap-1 truncate">
                                <Building2 className="h-3 w-3" />
                                {lead.company_name}
                              </span>
                            )}
                            {lead.viewed_at && (
                              <span className="flex items-center gap-1 text-slate-400 ml-auto flex-shrink-0">
                                <Clock className="h-3 w-3" />
                                {formatDistanceToNow(parseISO(lead.viewed_at), { addSuffix: true })}
                              </span>
                            )}
                          </div>
                          {lead.title && (
                            <p className="text-xs text-slate-400 truncate">{lead.title}</p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-400 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-2">
              {recentActivity.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Activity className="h-8 w-8 mx-auto mb-2" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {recentActivity.map((activity) => (
                    <button
                      key={activity.id}
                      onClick={() => {
                        if (activity.lead_id) {
                          // Bump this lead to top of recent list
                          try {
                            const stored = JSON.parse(localStorage.getItem('recentLeads') || '[]');
                            const entry = {
                              id: activity.lead_id,
                              first_name: (activity.lead_name || '').split(' ')[0] || '',
                              last_name: (activity.lead_name || '').split(' ').slice(1).join(' ') || '',
                              company_name: activity.company_name || '',
                              title: '',
                              viewed_at: new Date().toISOString()
                            };
                            const filtered = stored.filter(l => l.id !== activity.lead_id);
                            filtered.unshift(entry);
                            localStorage.setItem('recentLeads', JSON.stringify(filtered.slice(0, 20)));
                          } catch (e) { /* ignore */ }
                          navigate(`/admin/leads/${activity.lead_id}`);
                          setIsOpen(false);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg border transition-colors group ${getActivityColor(activity.type)} hover:opacity-90`}
                      data-testid={`recent-activity-${activity.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs capitalize">
                              {activity.type}
                            </Badge>
                            {activity.lead_name && (
                              <span className="text-sm font-medium text-slate-700 truncate">
                                {activity.lead_name}
                              </span>
                            )}
                            <EmailTrackingBadges comm={activity} compact />
                          </div>
                          {activity.content && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                              {activity.content.substring(0, 80)}...
                            </p>
                          )}
                          {activity.subject && (
                            <p className="text-xs text-slate-600 mt-1 truncate">
                              {activity.subject}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {activity.created_at && formatDistanceToNow(parseISO(activity.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Backdrop when open */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-20 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
